import * as React from "react";
import {
  PageHeader,
  Alert,
  Table,
  Button,
  Space,
  Input,
  Modal,
  Tag,
  Empty,
  message as antdMessage,
  Typography,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  BarcodeOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { InventoryClient, ProductStockEntryDto } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";
import { BarcodeScanResult } from "../hooks/useBarcodeScanner";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";
import { playAudioFeedback } from "../utils/audioFeedback";
import { useNotification } from "../hooks/useNotification";
import {
  parseProductBarcode,
  isPositionCode,
  extractPosition,
} from "../utils/barcodeUtils";
import {
  getStoragePositionKind,
  isQuarantineOrDamagedPosition,
  packingPositionOrderCode,
  StoragePositionKind,
} from "../utils/storagePositions";
import { CameraScanner } from "./shared/CameraScanner";
import { NumericKeyboard } from "./shared/NumericKeyboard";

const { Title, Text } = Typography;

// Initialize API client
const inventoryClient = new InventoryClient(new ApiConfig());
const STOCK_COMPARISON_EPSILON = 0.0001;

// LocalStorage key for session persistence
const STORAGE_KEY = "sku-stocktaking-session";

interface PositionStock {
  positionId: string;
  batchNumber: string;
  expectedQuantity: number;
  actualQuantity: number;
  isTemporary: boolean;
  isManuallyAdded?: boolean; // True for positions added by scanning QR codes
}

interface SkuStockData {
  skuId: number;
  productCode: string;
  productName?: string;
  unit?: string;
  amountOnStock?: number;
  amountOnStockAccounting?: number;
  positions: PositionStock[];
}

// Quarantine and damaged stock has to be counted like any other, but "CHK1"/"BAD1" sort after
// every rack position, so they used to end up buried at the bottom of a long list where the
// warehouse missed them entirely (SIG-227). Lift those to the top; everything else keeps the
// order the backend sent.
const sortPositions = (positions: PositionStock[]): PositionStock[] =>
  [...positions].sort(
    (a, b) => Number(isQuarantineOrDamagedPosition(b.positionId)) - Number(isQuarantineOrDamagedPosition(a.positionId))
  );

// What the stock-taking submits: everything but orders' boxes, which only packing changes (SIG-393).
const countedPositions = (positions: PositionStock[]): PositionStock[] =>
  positions.filter(pos => getStoragePositionKind(pos.positionId) !== "packing");

// Counted quantity, not the expected one, so the callout means what it says - the amount this
// stock-taking is about to submit. That also covers quarantine stock the system did not know
// about yet, which is exactly what a freshly scanned CHK1 position is.
const sumActualByKind = (positions: PositionStock[], kind: StoragePositionKind): number =>
  positions
    .filter(pos => getStoragePositionKind(pos.positionId) === kind)
    .reduce((sum, pos) => sum + pos.actualQuantity, 0);

// Minimal position data for localStorage
interface StoredPosition {
  positionId: string;
  batchNumber: string;
  actualQuantity: number;
  isManuallyAdded?: boolean;
}

interface StoredSession {
  productCode: string;
  positions: StoredPosition[];
  note?: string; // missing in sessions saved before notes existed (S-532)
}

const SkuStocktaking: React.FC = () => {
  // Core state
  const [skuData, setSkuData] = React.useState<SkuStockData | null>(null);
  const [note, setNote] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [cameraStatus, setCameraStatus] = React.useState<'idle' | 'initializing' | 'ready' | 'error'>('idle');

  // Current position for batch scanning
  const [currentPosition, setCurrentPosition] = React.useState<string | null>(null);

  // Flag to prevent saving during initial load
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Flag to track if user has performed any action (quantity change, add/remove position)
  // localStorage is saved only when this is true
  const [hasUserActivity, setHasUserActivity] = React.useState(false);

  // Notification system (floating messages, no layout shift)
  const notify = useNotification();

  // Combined feedback handler (visual + audio)
  const showCombinedFeedback = React.useCallback((msg: string, type: "success" | "error" | "info" | "warning") => {
    notify.notify(type, msg);
    if (type === "success" || type === "error") {
      playAudioFeedback(type);
    }
  }, [notify]);

  // Check if product uses batches (has any non-empty batch in positions)
  const productUsesBatches = React.useMemo(() => {
    return skuData?.positions.some(p => p.batchNumber !== "") ?? false;
  }, [skuData?.positions]);

  // Get status message for the status bar
  const getStatusMessage = React.useCallback((): { message: string; type: "info" | "warning" } => {
    if (isLoading) return { message: "Načítavam...", type: "info" };
    if (!skuData) return { message: "Naskenujte kód produktu", type: "info" };
    if (currentPosition) return {
      message: `Scanuj šarže na ${currentPosition}`,
      type: "warning"
    };
    return { message: "Scanuj pozície kde sa produkt nachádza", type: "info" };
  }, [isLoading, skuData, currentPosition]);

  // Add a new position+batch entry to the list
  const addPositionToList = React.useCallback((positionId: string, batchNumber: string, quantity: number) => {
    if (!skuData) return;

    // Check for duplicate position+batch
    const exists = skuData.positions.find(
      p => p.positionId === positionId && p.batchNumber === batchNumber
    );
    if (exists) {
      showCombinedFeedback(
        `Pozícia ${positionId}${batchNumber ? ` so šaržou ${batchNumber}` : ""} už existuje!`,
        "error"
      );
      return false;
    }

    const newPosition: PositionStock = {
      positionId,
      batchNumber,
      expectedQuantity: 0,
      actualQuantity: quantity,
      isTemporary: false,
      isManuallyAdded: true,
    };

    setSkuData({
      ...skuData,
      positions: sortPositions([...skuData.positions, newPosition]),
    });
    setHasUserActivity(true);

    const batchInfo = batchNumber ? ` (šarža: ${batchNumber})` : "";
    showCombinedFeedback(`Pridaná pozícia: ${positionId}${batchInfo}`, "success");
    return true;
  }, [skuData, showCombinedFeedback]);

  // Save current session to localStorage
  const saveToLocalStorage = React.useCallback(() => {
    try {
      if (!skuData) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const session: StoredSession = {
        productCode: skuData.productCode,
        positions: skuData.positions.map(pos => ({
          positionId: pos.positionId,
          batchNumber: pos.batchNumber,
          actualQuantity: pos.actualQuantity,
          isManuallyAdded: pos.isManuallyAdded,
        })),
        note,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error("Failed to save session to localStorage:", error);
    }
  }, [skuData, note]);

  // Clear localStorage
  const clearLocalStorage = React.useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  }, []);

  // Load saved session from localStorage on mount
  React.useEffect(() => {
    const loadFromLocalStorage = async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) {
          setIsInitialLoad(false);
          return;
        }

        const session: StoredSession = JSON.parse(saved);

        // Restore product and positions with fresh data from API
        await loadSkuLocations(session.productCode, session.positions, session.note);
        // Mark as having user activity since we restored a session
        if ((session.positions && session.positions.length > 0) || session.note) {
          setHasUserActivity(true);
        }
      } catch (error) {
        console.error("Failed to load session from localStorage:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadFromLocalStorage();
  }, []);

  // Auto-save to localStorage when state changes (after initial load and only if user has activity)
  React.useEffect(() => {
    if (!isInitialLoad && hasUserActivity) {
      saveToLocalStorage();
    }
  }, [isInitialLoad, hasUserActivity, saveToLocalStorage]);

  // Load SKU locations from backend
  const loadSkuLocations = async (productCode: string, restoredPositions?: StoredPosition[], restoredNote = "") => {
    setIsLoading(true);
    try {
      // Load product with all positions where it exists
      // Backend returns ProductStockEntriesResponseDto with product metadata
      const response = await inventoryClient.getStockEntriesByCode(productCode) as any;

      // Handle new response format with product metadata
      const skuId = response.SkuId || response.skuId;
      const productName = response.ProductName || response.productName;
      const unit = response.Unit || response.unit;
      const amountOnStock =
        response.AmountInWarehouse ?? response.amountInWarehouse ?? 0;
      const amountOnStockAccounting =
        response.AmountOnStockAccounting ?? response.amountOnStockAccounting ?? 0;
      const stockEntries = response.StockEntries || response.stockEntries || response;

      if (stockEntries.length === 0) {
        antdMessage.info(
          `Produkt ${productCode} sa nenachádza na žiadnej pozícii. Môžete pridať pozície manuálne.`
        );
      }

      // Transform to our data structure
      const positions: PositionStock[] = (Array.isArray(stockEntries) ? stockEntries : []).map((entry: any) => {
        const positionId = entry.PositionId || entry.positionId;
        const batchNumber = entry.BatchNumber || entry.batchNumber;

        // Check if we have a restored actualQuantity for this position+batch
        const restoredPos = restoredPositions?.find(
          p => p.positionId === positionId && p.batchNumber === batchNumber
        );

        return {
          positionId,
          batchNumber,
          expectedQuantity: entry.Quantity ?? entry.quantity ?? 0,
          // An order's box is not counted: it holds exactly what was scanned into it.
          actualQuantity: getStoragePositionKind(positionId) === "packing"
            ? entry.Quantity ?? entry.quantity ?? 0
            : restoredPos?.actualQuantity ?? entry.Quantity ?? entry.quantity ?? 0,
          isTemporary: entry.IsTemporary ?? entry.isTemporary ?? false,
          isManuallyAdded: restoredPos?.isManuallyAdded,
        };
      });

      // Add any manually-added positions from restored session that aren't in stock entries
      if (restoredPositions) {
        for (const restoredPos of restoredPositions) {
          if (restoredPos.isManuallyAdded) {
            const exists = positions.find(
              p => p.positionId === restoredPos.positionId && p.batchNumber === restoredPos.batchNumber
            );
            if (!exists) {
              positions.push({
                positionId: restoredPos.positionId,
                batchNumber: restoredPos.batchNumber,
                expectedQuantity: 0,
                actualQuantity: restoredPos.actualQuantity,
                isTemporary: false,
                isManuallyAdded: true,
              });
            }
          }
        }
      }

      setSkuData({
        skuId,
        productCode: response.ProductCode || response.productCode || productCode,
        productName,
        unit,
        amountOnStock,
        amountOnStockAccounting,
        positions: sortPositions(positions),
      });
      // The note belongs to the product on screen, so a newly loaded product starts without one.
      setNote(restoredNote);

      if (stockEntries.length > 0) {
        showCombinedFeedback(
          `Načítané ${stockEntries.length} ${stockEntries.length === 1 ? 'pozícia' : stockEntries.length < 5 ? 'pozície' : 'pozícií'} pre ${productCode}`,
          "success"
        );
      } else {
        showCombinedFeedback(
          `Načítaný produkt ${productCode}. Pridajte pozície naskenovaním QR kódov.`,
          "success"
        );
      }
    } catch (error) {
      console.error("Failed to load SKU locations:", error);
      const errorMsg = getApiErrorMessage(error);
      antdMessage.error(errorMsg);
      showCombinedFeedback(errorMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if there are any unsaved changes
  const hasUnsavedChanges = React.useCallback((): boolean => {
    if (!skuData) return false;

    // Check if any actual quantities differ from expected, or a note was written
    return note.trim() !== "" || skuData.positions.some(pos => pos.actualQuantity !== pos.expectedQuantity);
  }, [skuData, note]);

  // Handle scanned code processing
  const processScannedCode = React.useCallback(async (code: string) => {
    if (!code) return;

    // Check if it's a position code (ends with @)
    if (isPositionCode(code)) {
      // Adding a position - need a product loaded first
      if (!skuData) {
        showCombinedFeedback("Najprv naskenujte produkt!", "error");
        return;
      }

      const position = extractPosition(code);
      if (!position) {
        showCombinedFeedback("Neplatná pozícia!", "error");
        return;
      }

      // Set current position for batch scanning
      setCurrentPosition(position);
      showCombinedFeedback(`Pozícia ${position} nastavená. Scanujte šarže.`, "info");
      setHasUserActivity(true);
      return;
    }

    // Otherwise, it's a product code
    const parsed = parseProductBarcode(code);
    if (!parsed) {
      showCombinedFeedback("Neplatný formát kódu produktu!", "error");
      return;
    }

    // If we have a current position and the scanned product matches loaded product
    // → add position+batch entry
    if (currentPosition && skuData && parsed.productCode === skuData.productCode) {
      // Validate: if product uses batches, batch is required
      if (productUsesBatches && !parsed.batchNumber) {
        showCombinedFeedback("Tento produkt vyžaduje šaržu. Naskenujte produkt so šaržou.", "error");
        return;
      }

      addPositionToList(currentPosition, parsed.batchNumber, parsed.quantity);
      // Don't clear currentPosition - user may want to add more batches to same position
      return;
    }

    // Loading a new product
    // Check if we have unsaved changes
    if (skuData && hasUnsavedChanges()) {
      Modal.confirm({
        title: "Neuložené zmeny",
        content: `Máte neuložené zmeny pre produkt ${skuData.productCode}. Chcete pokračovať a načítať nový produkt ${parsed.productCode}? Zmeny budú stratené.`,
        okText: "Áno, načítať nový produkt",
        cancelText: "Nie, zostať tu",
        okButtonProps: { danger: true },
        onOk: async () => {
          setCurrentPosition(null);
          await loadSkuLocations(parsed.productCode);
        },
      });
      return;
    }

    setCurrentPosition(null);
    await loadSkuLocations(parsed.productCode);
  }, [skuData, currentPosition, productUsesBatches, showCombinedFeedback, addPositionToList, hasUnsavedChanges]);

  // Prevent navigation when there are unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Camera barcode scanner
  const handleCameraScan = React.useCallback(async (result: BarcodeScanResult) => {
    await processScannedCode(result.rawValue);
  }, [processScannedCode]);

  // Keyboard scanner (HID scanner)
  useKeyboardScanner(processScannedCode);

  // Update actual quantity for a position
  const updateActualQuantity = (positionId: string, batchNumber: string, newQuantity: number) => {
    if (!skuData) return;

    setSkuData({
      ...skuData,
      positions: skuData.positions.map(pos =>
        pos.positionId === positionId && pos.batchNumber === batchNumber
          ? { ...pos, actualQuantity: newQuantity }
          : pos
      ),
    });
    setHasUserActivity(true);
  };

  // Remove a position from the list
  const removePosition = (positionId: string, batchNumber: string) => {
    if (!skuData) return;

    setSkuData({
      ...skuData,
      positions: skuData.positions.filter(
        pos => !(pos.positionId === positionId && pos.batchNumber === batchNumber)
      ),
    });
    setHasUserActivity(true);
  };


  const updateNote = (newNote: string) => {
    setNote(newNote);
    setHasUserActivity(true);
  };

  // Calculate totals
  const getTotals = () => {
    if (!skuData) return { expected: 0, actual: 0, difference: 0 };

    const expected = skuData.positions.reduce((sum, pos) => sum + pos.expectedQuantity, 0);
    const actual = skuData.positions.reduce((sum, pos) => sum + pos.actualQuantity, 0);
    const difference = actual - expected;

    return { expected, actual, difference };
  };

  // Submit the stocktaking
  const handleSubmit = async () => {
    if (!skuData) {
      antdMessage.error("Najprv načítajte produkt!");
      return;
    }

    // Validate: if product uses batches, all manually added positions must have batch
    if (productUsesBatches) {
      const positionsWithoutBatch = skuData.positions.filter(
        p => p.isManuallyAdded && p.batchNumber === ""
      );
      if (positionsWithoutBatch.length > 0) {
        const positionNames = positionsWithoutBatch.map(p => p.positionId).join(", ");
        showCombinedFeedback(
          `Pozície ${positionNames} nemajú zadanú šaržu. Tento produkt vyžaduje šaržu.`,
          "error"
        );
        return;
      }
    }

    // Allow submitting with no positions - confirm product not found in warehouse
    if (skuData.positions.length === 0) {
      Modal.confirm({
        title: "Potvrdiť nulový stav skladu",
        content: (
          <div>
            <p>Produkt <strong>{skuData.productCode}</strong> {skuData.productName && `(${skuData.productName})`} nemá žiadne pozície v zozname.</p>
            <p>Chcete potvrdiť, že tento produkt sa <strong>nenachádza v sklade vôbec</strong>?</p>
          </div>
        ),
        okText: "Áno, produkt nie je na sklade",
        cancelText: "Nie, pridať pozície",
        onOk: async () => {
          await submitSkuStocktaking();
        },
      });
      return;
    }

    await submitSkuStocktaking();
  };

  // Internal submit function
  const submitSkuStocktaking = async () => {
    if (!skuData) return;

    setIsSubmitting(true);

    try {
      await inventoryClient.processSkuStockTaking({
        SkuId: skuData.skuId,
        SkuCode: skuData.productCode,
        // Orders' boxes are shown so the total matches Pohoda, but they are not part of the count.
        Items: countedPositions(skuData.positions).map(pos => ({
          StoragePositionId: pos.positionId,
          SkuId: skuData.skuId,
          SkuCode: skuData.productCode,
          BatchNumber: pos.batchNumber,
          ExpectedQuantity: pos.expectedQuantity,
          ActualQuantity: pos.actualQuantity,
        })),
        Note: note.trim() || null,
      });

      const countedCount = countedPositions(skuData.positions).length;
      if (countedCount === 0) {
        antdMessage.success(`Produkt ${skuData.productCode} označený ako nenájdený v sklade`);
      } else {
        antdMessage.success(
          `Inventúra SKU ${skuData.productCode} uložená (${countedCount} ${countedCount === 1 ? 'pozícia' : countedCount < 5 ? 'pozície' : 'pozícií'})`
        );
      }

      // Reset for next SKU
      setSkuData(null);
      setNote("");
      setCurrentPosition(null);
      setHasUserActivity(false);
      clearLocalStorage();
      showCombinedFeedback("Inventúra uložená! Naskenujte ďalší produkt.", "success");
    } catch (error) {
      console.error("Stocktaking submission failed:", error);
      const errorMsg = getApiErrorMessage(error);
      antdMessage.error(errorMsg);
      showCombinedFeedback(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset current stocktaking
  const handleReset = () => {
    Modal.confirm({
      title: "Zrušiť aktuálnu inventúru?",
      content: skuData
        ? `Všetky údaje pre ${skuData.productCode} budú odstránené.`
        : "Všetky údaje budú odstránené.",
      okText: "Áno, zrušiť",
      cancelText: "Nie",
      onOk: () => {
        setSkuData(null);
        setNote("");
        setCurrentPosition(null);
        setHasUserActivity(false);
        clearLocalStorage();
        notify.destroy(); // Clear any active notifications
      },
    });
  };

  // Calculate difference for a position
  const getDifference = (pos: PositionStock): number => {
    return pos.actualQuantity - pos.expectedQuantity;
  };

  // Table columns
  const columns = [
    {
      title: "Pozícia",
      key: "position",
      render: (pos: PositionStock) => {
        const kind = getStoragePositionKind(pos.positionId);
        return (
          // The column is narrow on a handheld, so let the tags wrap under the position
          // instead of breaking the position ID itself into one character per line.
          <Space wrap size={4}>
            <Space size={4} style={{ whiteSpace: "nowrap" }}>
              <EnvironmentOutlined />
              <Text strong>{pos.positionId}</Text>
            </Space>
            {kind === "quarantine" && <Tag color="gold">Na kontrolu</Tag>}
            {kind === "damaged" && <Tag color="red">Poškodené</Tag>}
            {kind === "packing" && <Tag color="purple">Balík objednávky {packingPositionOrderCode(pos.positionId)}</Tag>}
            {pos.isTemporary && <Tag color="orange">Dočasná</Tag>}
            {pos.isManuallyAdded && <Tag color="green">Nová</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Šarža",
      dataIndex: "batchNumber",
      key: "batchNumber",
      render: (text: string) => text || "-",
    },
    {
      title: "Očakávané",
      key: "expectedQuantity",
      render: (pos: PositionStock) => (
        <Text>
          {pos.expectedQuantity} {skuData?.unit || ""}
        </Text>
      ),
    },
    {
      title: "Skutočné",
      key: "actualQuantity",
      render: (pos: PositionStock) =>
        getStoragePositionKind(pos.positionId) === "packing" ? (
          // Not counted - it holds exactly what the packer scanned into it.
          <Text>
            {pos.actualQuantity} {skuData?.unit}
          </Text>
        ) : (
          <NumericKeyboard
            title="Zadajte skutočné množstvo"
            unit={skuData?.unit}
            value={pos.actualQuantity}
            onChange={(value) => updateActualQuantity(pos.positionId, pos.batchNumber, value)}
            onEnter={() => {
              // Close popover
            }}
          >
            <Button style={{ width: "100%", minWidth: "90px", textAlign: "left" }}>
              {pos.actualQuantity} {skuData?.unit}
            </Button>
          </NumericKeyboard>
        ),
    },
    {
      title: "Rozdiel",
      key: "difference",
      render: (pos: PositionStock) => {
        const diff = getDifference(pos);
        const color = diff === 0 ? "green" : diff > 0 ? "blue" : "red";
        const prefix = diff > 0 ? "+" : "";

        return (
          <Tag color={color}>
            {prefix}{diff} {skuData?.unit || ""}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (pos: PositionStock) => (
        <Button
          danger
          size="small"
          shape="circle"
          icon={<DeleteOutlined />}
          onClick={() => removePosition(pos.positionId, pos.batchNumber)}
          disabled={isSubmitting || getStoragePositionKind(pos.positionId) === "packing"}
        />
      ),
    },
  ];

  const totals = getTotals();
  const accountingStock = skuData?.amountOnStockAccounting ?? 0;
  // Derived from the rows on screen, not from the response, so the callout can never claim
  // stock the stock-taking is no longer going to submit.
  const quarantineStock = skuData ? sumActualByKind(skuData.positions, "quarantine") : 0;
  const damagedStock = skuData ? sumActualByKind(skuData.positions, "damaged") : 0;
  const stockComparisonDifference = skuData ? totals.expected - accountingStock : 0;
  const stockEntriesMatchPohoda = skuData
    ? Math.abs(stockComparisonDifference) < STOCK_COMPARISON_EPSILON
    : false;

  // Handle back navigation with unsaved changes check
  const handleBack = () => {
    if (hasUnsavedChanges()) {
      Modal.confirm({
        title: "Neuložené zmeny",
        content: `Máte neuložené zmeny pre produkt ${skuData?.productCode}. Chcete opustiť stránku? Zmeny budú stratené.`,
        okText: "Áno, opustiť",
        cancelText: "Nie, zostať tu",
        okButtonProps: { danger: true },
        onOk: () => {
          window.history.back();
        },
      });
    } else {
      window.history.back();
    }
  };

  return (
    <div style={{ padding: "0 0 80px 0", paddingBottom: cameraStatus !== 'idle' ? 'calc(25vh + 140px)' : '140px' }}>
      <PageHeader
        className="site-page-header"
        onBack={handleBack}
        title="Inventúra SKU"
      />

      {/* Instructions */}
      <Alert
        message="Ako používať inventúru SKU"
        description={
          <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Naskenujte <strong>kód produktu</strong> (SKU) ktorý chcete zinventarizovať</li>
            <li>Systém načíta <strong>všetky pozície</strong> kde by sa produkt mal nachádzať</li>
            <li>Upravte <strong>skutočné množstvá</strong> pre každú pozíciu a šaržu</li>
            <li>Voliteľne pridajte novú pozíciu naskenovaním QR kódu pozície</li>
            <li>Kliknite <strong>Uložiť inventúru</strong> pre odoslanie</li>
          </ol>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Current Product Info */}
      {skuData && (
        <div
          style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: hasUnsavedChanges() ? "#fff7e6" : "#e6f7ff",
            border: `1px solid ${hasUnsavedChanges() ? "#ffa940" : "#91d5ff"}`,
            borderRadius: 2,
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <BarcodeOutlined style={{ fontSize: 20, marginRight: 8 }} />
              <Text strong style={{ fontSize: 18 }}>{skuData.productCode}</Text>
              {skuData.productName && (
                <Text style={{ marginLeft: 8, color: "#666" }}> - {skuData.productName}</Text>
              )}
              {hasUnsavedChanges() && (
                <Tag color="orange" style={{ marginLeft: 8 }}>Neuložené zmeny</Tag>
              )}
            </div>
            <Alert
              type={stockEntriesMatchPohoda ? "success" : "error"}
              showIcon
              message={stockEntriesMatchPohoda ? "Súčet na policiach sedí s Pohodou" : "Súčet na policiach nesedí s Pohodou"}
              description={
                <Space size="large" wrap>
                  <Text>
                    Police: <Text strong>{totals.expected} {skuData.unit}</Text>
                  </Text>
                  <Text>
                    Pohoda: <Text strong>{accountingStock} {skuData.unit}</Text>
                  </Text>
                  <Text type={stockEntriesMatchPohoda ? "success" : "danger"}>
                    Rozdiel: <Text strong>{stockComparisonDifference > 0 ? "+" : ""}{stockComparisonDifference} {skuData.unit}</Text>
                  </Text>
                </Space>
              }
            />
            {(quarantineStock > 0 || damagedStock > 0) && (
              <Alert
                type="warning"
                showIcon
                message="Časť tovaru nie je k dispozícii zákazníkom - inventúra ju napriek tomu zahŕňa"
                description={
                  <Space size="large" wrap>
                    {quarantineStock > 0 && (
                      <Text>
                        Na kontrolu (CHK): <Text strong>{quarantineStock} {skuData.unit}</Text>
                      </Text>
                    )}
                    {damagedStock > 0 && (
                      <Text>
                        Poškodené (BAD): <Text strong>{damagedStock} {skuData.unit}</Text>
                      </Text>
                    )}
                  </Space>
                }
              />
            )}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Police celkom"
                  value={skuData.amountOnStock ?? 0}
                  suffix={skuData.unit}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Pohoda"
                  value={accountingStock}
                  suffix={skuData.unit}
                  valueStyle={{ color: stockEntriesMatchPohoda ? "#3f8600" : "#cf1322" }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Očakávané celkom"
                  value={totals.expected}
                  suffix={skuData.unit}
                  valueStyle={{ color: stockEntriesMatchPohoda ? "#3f8600" : "#cf1322" }}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Skutočné celkom"
                  value={totals.actual}
                  suffix={skuData.unit}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Rozdiel"
                  value={totals.difference}
                  suffix={skuData.unit}
                  valueStyle={{ color: totals.difference === 0 ? '#3f8600' : totals.difference > 0 ? '#1890ff' : '#cf1322' }}
                  prefix={totals.difference > 0 ? "+" : ""}
                />
              </Col>
            </Row>
          </Space>
        </div>
      )}

      {/* Positions Table */}
      {skuData && (
        <Table
          dataSource={skuData.positions}
          columns={columns}
          rowKey={(pos) => `${pos.positionId}-${pos.batchNumber}`}
          pagination={false}
          loading={isLoading}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Tento produkt nemá známe pozície"
              />
            ),
          }}
          style={{ marginBottom: 16 }}
        />
      )}

      {skuData && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="sku-stocktaking-note" style={{ fontWeight: 600 }}>
            Poznámka
          </label>
          <Input.TextArea
            id="sku-stocktaking-note"
            value={note}
            onChange={(e) => updateNote(e.target.value)}
            placeholder="Voliteľné, uloží sa s inventúrou SKU"
            autoSize={{ minRows: 2, maxRows: 6 }}
            enterKeyHint="done"
            // Enter finishes the note instead of starting a new line: a barcode scanned while the note
            // still has focus would otherwise vanish into it, along with every scan after it.
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            disabled={isSubmitting}
            style={{ marginTop: 4 }}
          />
        </div>
      )}

      {/* Action Buttons - Fixed at bottom */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: "16px",
          zIndex: 100,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "600px" }}>
          {/* Status bar */}
          {(() => {
            const status = getStatusMessage();
            return (
              <div style={{
                marginBottom: 12,
                padding: "8px 12px",
                backgroundColor: status.type === "warning" ? "#fffbe6" : "#e6f7ff",
                border: `1px solid ${status.type === "warning" ? "#ffe58f" : "#91d5ff"}`,
                borderRadius: 4,
                textAlign: "center",
              }}>
                <Text type={status.type === "warning" ? "warning" : "secondary"}>
                  {status.message}
                </Text>
              </div>
            );
          })()}

          <style>{`
            .sku-stocktaking-button-row .ant-space-item:last-child {
              flex-grow: 1;
            }
          `}</style>

          <Space className="sku-stocktaking-button-row" style={{ width: "100%", display: "flex" }}>
            {/* Camera Scanner - always mounted */}
            <CameraScanner
              onScan={handleCameraScan}
              onCameraStatusChange={setCameraStatus}
              buttonSize="large"
            />

            {/* Reset and Submit buttons - hidden when camera is active */}
            {cameraStatus === 'idle' && (
              <>
                {/* Reset Button */}
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={isSubmitting || isLoading || !skuData}
                  style={{ height: 48 }}
                />

                {/* Submit Button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={!skuData || isLoading}
                  block
                  style={{ height: 48 }}
                >
                  {isSubmitting ? "Ukladám..." : "Uložiť inventúru"}
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

export default SkuStocktaking;
